import React, { useState } from "react";
import "./OrderDetails.css";
import { Download, ChevronDown, ChevronUp, ArrowLeft } from "lucide-react";
import { useOrderDetails } from "../../customHooks/Orders/useOrderDetails";
import {
  formatCurrency,
  formatDate,
  getStatusIcon,
  formatDateTime,
  mapStatusToBootstrap,
} from "../../components/Constants/Constants";
import AppLoader from "../../components/AppLoader/appLoader";
import usePermission from "../../utils/usePermission";
import UnAuthorized from "../UnAuthorized/UnAuthorized";
import { useSelector } from "react-redux";

function OrderDetails({ orderId, hideHeaderSection }) {
  const {
    orderData,
    loading,
    error,
    isReturnExpired,
    handleGetInvoice,
    handleSettlementRequest,
    handleRaiseRecon,
    handleGoBack
  } = useOrderDetails({ orderId });

  const canViewOrdersDetail = usePermission("ORDER_DETAILS_VIEW");
  const canAccessSettlement = usePermission("REQUEST_SETTLEMENT_ACCESS");
  const canRaiseRecon = usePermission("RAISE_RECON_ACCESS");

  const [expanded, setExpanded] = useState({
    overview: true,
    buyer: true,
    seller: false,
  });

const toggleSection = (section) => {
  setExpanded((prev) => ({
    ...prev, 
    [section]: !prev[section],
  }));
};

  return (
    <>
      {canViewOrdersDetail ? (
        <div>
          {loading ? (
            <AppLoader />
          ) : (
            <>
              <div className="min-vh-100 bg-light">
                {/* Header */}
                {!hideHeaderSection && (
                  <div className="mb-2">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          <button
                            className="btn btn-link text-primary p-0 d-flex align-items-center text-decoration-none me-3"
                            onClick={handleGoBack}
                            style={{ fontSize: "16px", fontWeight: "500" }}
                            aria-label="Go back to orders list"
                          >
                            <ArrowLeft size={20} style={{ marginRight: "5px" }} />
                            Back
                          </button>
                          <div>
                            <p
                              className="mb-0 small"
                              style={{
                                fontSize: "16px",
                                marginLeft: "5px",
                                fontWeight: "500",
                              }}
                            >
                              Order ID:{" "}
                              <span
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "500",
                                  marginLeft: "5px",
                                }}
                              >
                                {orderData?.order_details?.order_id}
                              </span>
                            </p>
                          </div>
                        </div>

                        <div className="d-flex gap-2">
                          {/* <button
                            className="btn btn-warning d-flex align-items-center"
                            style={{
                              padding: "4px 8px",
                              fontSize: "14px",
                              marginTop: "8px",
                            }}
                            onClick={handleRaiseRecon}
                            disabled={!canRaiseRecon}
                          >
                            Raise Recon
                          </button> */}
                          <button
                            className="btn btn-primary d-flex align-items-center"
                            style={{
                              padding: "4px 8px",
                              fontSize: "14px",
                              marginTop: "8px",
                            }}
                            onClick={handleSettlementRequest}
                            disabled={!canAccessSettlement}
                          >
                            Request Settlement
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Main Content */}
                <div className="row g-4">
                  {/* Left Panel - Order Items */}
                  <div className="col-lg-8">
                    <div className="card">
                      <div className="card-header bg-white d-flex justify-content-between align-items-center">
                        <span
                          style={{ fontSize: "18px", fontWeight: "600" }}
                          className="mb-0"
                        >
                          Order Items
                        </span>
                        <button
                          className="btn btn-link text-primary p-0 d-flex align-items-center text-decoration-none"
                          onClick={handleGetInvoice}
                        >
                          <Download size={16} className="me-1" />
                          Download Invoice
                        </button>
                      </div>

                      {/* Order Items List */}
                      <div className="card-body">
                        <div className="row g-3">
                          <div className="col-12 order-items-scroll">
                            {orderData?.order_summary?.products.map(
                              (item, index) => (
                                <div key={index} className="mb-1">
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                      <h6 className="mb-1">{item.item_name}</h6>
                                      <div className="d-flex gap-4">
                                        <small
                                          style={{ fontSize: "12px" }}
                                          className="text-muted"
                                        >
                                          Price:{" "}
                                          {formatCurrency(item.unit_price)}
                                        </small>
                                        <small
                                          style={{ fontSize: "12px" }}
                                          className="text-muted"
                                        >
                                          Quantity: {item.quantity}
                                        </small>
                                      </div>
                                    </div>
                                    <div className="text-end">
                                      <div className="fw-bold">
                                        {formatCurrency(
                                          item.quantity * item.unit_price
                                        )}
                                      </div>

                                      {item.product_gst_percentage !== 0 &&
                                        !item.is_product_gst_inclusive && (
                                          <>
                                            <div
                                              style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                fontSize: "12px",
                                                gap: "50px",
                                              }}
                                            >
                                              <span className="text-muted">
                                                {item.product_gst_percentage}%
                                                GST
                                              </span>
                                              <span>
                                                {item.calc_product_gst_amount}
                                              </span>
                                            </div>
                                          </>
                                        )}
                                    </div>
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </div>

                        {/* Financial Summary */}
                        <div className="mt-2 pt-3 border-top">
                          <div className="row g-2">
                            {/* Sub Total */}
                            <div
                              className="col-12 d-flex justify-content-between"
                              style={{ fontSize: "14px" }}
                            >
                              <span>Sub Total</span>
                              <span>
                                {formatCurrency(
                                  orderData?.order_summary
                                    ?.sub_total_amount_with_gst
                                )}
                              </span>
                            </div>

                            {orderData?.order_summary
                              ?.is_convenience_fee_applied === true && (
                              <div
                                className="col-12 d-flex justify-content-between"
                                style={{ fontSize: "14px" }}
                              >
                                <span>Convenience Fee</span>
                                <div className="text-end">
                                  <div>
                                    {formatCurrency(
                                      orderData?.order_summary?.convenience_fee
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      fontSize: "12px",
                                      gap: "50px",
                                    }}
                                  >
                                    <span className="text-muted">
                                      {" "}
                                      {
                                        orderData?.order_summary
                                          ?.convenience_gst_percentage
                                      }
                                      % GST
                                    </span>
                                    <span>
                                      {
                                        orderData?.order_summary
                                          ?.gst_convenience_fee
                                      }
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Delivery Charges (Only show if GST is not 0) */}
                            {orderData?.order_summary
                              ?.gst_on_delivery_charges !== 0 && (
                              <div
                                className="col-12 d-flex justify-content-between"
                                style={{ fontSize: "14px" }}
                              >
                                <span>Delivery Charges</span>
                                <div className="text-end">
                                  <div>
                                    {formatCurrency(
                                      orderData?.order_summary?.delivery_charges
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      fontSize: "12px",
                                      gap: "50px",
                                    }}
                                  >
                                    <span className="text-muted">
                                      {" "}
                                      {
                                        orderData?.order_summary
                                          ?.delivery_charges_gst_percentage
                                      }
                                      % GST
                                    </span>
                                    <span>
                                      {
                                        orderData?.order_summary
                                          ?.gst_on_delivery_charges
                                      }
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}
                            <div className="mt-2 pt-3 border-top">
                              {/* Buyer Fee On Order Value (Only show if GST is not 0) */}
                              {orderData?.order_summary
                                ?.buyer_finder_fee_Percent_on_order_value !==
                                0 && (
                                <div
                                  className="col-12 d-flex justify-content-between"
                                  style={{ fontSize: "14px" }}
                                >
                                  <span>Buyer Fee On Order Value</span>
                                  <div className="text-end">
                                    <div>
                                      {formatCurrency(
                                        orderData?.order_summary
                                          ?.buyer_finder_fee_on_order_amount
                                      )}
                                    </div>

                                    <span className="text-muted">
                                      {
                                        orderData?.order_summary
                                          ?.buyer_finder_fee
                                      }
                                      % BFF
                                    </span>
                                  </div>
                                </div>
                              )}

                              {orderData?.order_summary
                                ?.buyer_finder_fee_on_delivery_charges !==
                                0 && (
                                <div
                                  className="col-12 d-flex justify-content-between"
                                  style={{ fontSize: "14px" }}
                                >
                                  <span>Buyer Fee On Delivery Charges</span>
                                  <span>
                                    {formatCurrency(
                                      orderData?.order_summary
                                        ?.buyer_finder_fee_on_delivery_charges
                                    )}
                                  </span>
                                </div>
                              )}

                              <div className="col-12 d-flex justify-content-between fw-bold text-danger">
                                <span>Total Buyer Finder Fee</span>
                                <span>
                                  {formatCurrency(
                                    orderData?.order_summary
                                      ?.total_buyer_finder_fee
                                  )}
                                </span>
                              </div>
                            </div>

                            {/* Total */}
                            <div className="col-12 border-top pt-2">
                              <div className="d-flex justify-content-between fw-bold fs-5">
                                <span>Total</span>
                                <span>
                                  {formatCurrency(
                                    orderData?.order_summary?.total_order_amount
                                  )}
                                </span>
                              </div>
                            </div>

                            {/* Amount to be Settle */}
                            <div className="col-12 d-flex justify-content-between fw-bold text-success">
                              <span>Amount To Be Settle</span>
                              <span>
                                {formatCurrency(
                                  orderData?.order_summary?.amount_to_be_settle
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Panel */}
                  <div className="col-lg-4">
                    <div className="row g-4">
                      {/* Order Overview */}
                      <div className="col-12">
                        <div className="card">
                          <div
                            className="card-header bg-white d-flex justify-content-between align-items-center"
                            onClick={() => toggleSection("overview")}
                            style={{ cursor: "pointer" }}
                          >
                            <span
                              style={{ fontSize: "18px", fontWeight: "600" }}
                              className="mb-0"
                            >
                              Order Overview
                            </span>
                            {expanded.overview ? (
                              <ChevronUp size={16} />
                            ) : (
                              <ChevronDown size={16} />
                            )}
                          </div>
                          {expanded.overview && (
                            <div className="card-body">
                              <div className="row g-2">
                                <div className="col-12 d-flex justify-content-between align-items-center">
                                  <span className="small">Order Status</span>
                                  <span
                                    className={mapStatusToBootstrap(
                                      orderData?.order_details?.order_status
                                    )}
                                  >
                                    {getStatusIcon(
                                      orderData?.order_details?.order_status
                                    )}
                                    &nbsp;
                                    {
                                      orderData?.order_details?.order_status
                                    }{" "}
                                  </span>
                                </div>
                                <div className="col-12 d-flex justify-content-between align-items-center">
                                  <span className="small">Order Category</span>
                                  <span className="small">
                                    {orderData?.order_details?.order_category}
                                  </span>
                                </div>
                                <div className="col-12 d-flex justify-content-between align-items-center">
                                  <span className="small">Transaction Id</span>
                                  <span className="small font-monospace">
                                    {
                                      orderData?.buyer_details
                                        ?.order_transaction_id
                                    }
                                  </span>
                                </div>
                                <div className="col-12 d-flex justify-content-between align-items-center">
                                  <span className="small">Return Window</span>
                                  <span className="small">
                                    {orderData?.order_details?.return_window}{" "}
                                    days
                                  </span>
                                </div>
                                <div className="col-12 d-flex justify-content-between align-items-center">
                                  <span className="small">Return Expiry</span>
                                  <div className="text-end">
                                    <div className="small">
                                      {formatDateTime(
                                        orderData?.order_details
                                          ?.return_expires_at
                                      )}
                                    </div>
                                    {isReturnExpired() && (
                                      <small className="text-danger fw-bold">
                                        Expired
                                      </small>
                                    )}
                                  </div>
                                </div>
                                <div className="col-12 d-flex justify-content-between align-items-center">
                                  <span className="small">
                                    Order Created At
                                  </span>
                                  <span className="small">
                                    {formatDateTime(
                                      orderData?.order_details?.order_created_at
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Buyer Details */}
                      <div className="col-12">
                        <div className="card">
                          <div
                            className="card-header bg-white d-flex justify-content-between align-items-center"
                            onClick={() => toggleSection("buyer")}
                            style={{ cursor: "pointer" }}
                          >
                            <span
                              style={{ fontSize: "18px", fontWeight: "600" }}
                              className="mb-0"
                            >
                              Buyer Details
                            </span>
                            {expanded.buyer ? (
                              <ChevronUp size={16} />
                            ) : (
                              <ChevronDown size={16} />
                            )}
                          </div>
                          {expanded.buyer && (
                            <div className="card-body">
                              <div className="row g-3">
                                {/* <div className="col-12 d-flex justify-content-between align-items-center">
                          <span className="small">
                            Buyer Order Id
                          </span>
                          <span className="small">
                            {orderData?.buyer_details?.buyer_order_id}
                          </span>
                        </div> */}

                                <div className="col-12 d-flex justify-content-between align-items-center">
                                  <span className="small">Buyer App</span>
                                  <span className="small">
                                    {orderData?.buyer_details?.buyer_app_id}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Seller Details */}
                      <div className="col-12">
                        <div className="card">
                          <div
                            className="card-header bg-white d-flex justify-content-between align-items-center"
                            onClick={() => toggleSection("seller")}
                            style={{ cursor: "pointer" }}
                          >
                            <span
                              style={{ fontSize: "18px", fontWeight: "600" }}
                              className="mb-0"
                            >
                              Seller Details
                            </span>
                            {expanded.seller ? (
                              <ChevronUp size={16} />
                            ) : (
                              <ChevronDown size={16} />
                            )}
                          </div>
                          {expanded.seller && (
                            <div className="card-body">
                              <div className="row g-3">
                                <div className="col-12 d-flex justify-content-between align-items-center">
                                  <span className="small">Seller Order Id</span>
                                  <span className="small">
                                    {orderData?.seller_details?.seller_order_id}
                                  </span>
                                </div>
                                <div className="col-12 d-flex justify-content-between align-items-center">
                                  <span className="small">Seller App</span>
                                  <span className="small">
                                    {orderData?.seller_details?.seller_app_id}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-12">
                        <div className="card">
                          <div
                            className="card-header bg-white d-flex justify-content-between align-items-center"
                            onClick={() => toggleSection("provider")}
                            style={{ cursor: "pointer" }}
                          >
                            <span
                              style={{ fontSize: "18px", fontWeight: "600" }}
                              className="mb-0"
                            >
                              Provider Details
                            </span>
                            {expanded.provider ? (
                              <ChevronUp size={16} />
                            ) : (
                              <ChevronDown size={16} />
                            )}
                          </div>
                          {expanded.provider && (
                            <div className="card-body">
                              <div className="row g-3">
                                <div className="col-12 d-flex justify-content-between align-items-center">
                                  <span className="small">Provider ID</span>
                                  <span className="small">
                                    {orderData?.provider?.provider_id}
                                  </span>
                                </div>
                                <div className="col-12 d-flex justify-content-between align-items-center">
                                  <span className="small">Provider Name</span>
                                  <span className="small">
                                    {orderData?.provider?.provider_name}
                                  </span>
                                </div>
                                <div className="col-12 d-flex justify-content-between align-items-center">
                                  <span className="small">Bank Account No</span>
                                  <span className="small">
                                    {
                                      orderData?.provider?.bank_details
                                        ?.account_no
                                    }
                                  </span>
                                </div>
                                <div className="col-12 d-flex justify-content-between align-items-center">
                                  <span className="small">IFSC Code</span>
                                  <span className="small">
                                    {
                                      orderData?.provider?.bank_details
                                        ?.ifsc_code
                                    }
                                  </span>
                                </div>
                                <div className="col-12 d-flex justify-content-between align-items-center">
                                  <span className="small">Amount</span>
                                  <span className="small">
                                    {orderData?.provider?.amount}
                                  </span>
                                </div>
                                <div className="col-12 d-flex justify-content-between align-items-center">
                                  <span className="small">
                                    Provider Settlement Status
                                  </span>
                                  <span className="small">
                                    {
                                      orderData?.provider
                                        ?.provider_settlement_status
                                    }
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {Array.isArray(orderData?.settlements) &&
                orderData?.settlements.length > 0 ? (
                  <div className="mt-4">
                    <div className="card">
                      <div className="card-header bg-white d-flex justify-content-between align-items-center">
                        <span
                          style={{ fontSize: "18px", fontWeight: "600" }}
                          className="mb-0"
                        >
                          Settlement Breakdown
                        </span>
                      </div>

                      <div className="card-body">
                        <div className="d-flex justify-content-between flex-wrap gap-3 mb-1 px-1 small text-muted">
                          <div className="me-3">
                            <strong className="text-dark">
                              Amount to be Settled:
                            </strong>{" "}
                            ₹
                            {orderData?.settlement_summary?.amount_to_be_settle}
                          </div>
                          <div className="me-3">
                            <strong className="text-dark">
                              Settled Amount:
                            </strong>{" "}
                            ₹{orderData?.settlement_summary?.settled_amount}
                          </div>
                          <div>
                            <strong className="text-dark">
                              Overall Status:
                            </strong>{" "}
                            <span
                              className={mapStatusToBootstrap(
                                orderData?.settlement_summary?.settlement_status
                              )}
                            >
                              {getStatusIcon(
                                orderData?.settlement_summary?.settlement_status
                              )}
                              &nbsp;
                              {orderData?.settlement_summary?.settlement_status}
                            </span>
                          </div>
                        </div>

                        <div
                          className="table-responsive"
                          style={{ maxHeight: "500px", overflowY: "auto" }}
                        >
                          <table
                            className="table table-sm table-bordered align-middle mb-0"
                            style={{ width: "100%" }}
                          >
                            <thead className="table-light">
                              <tr>
                                <th>Settlement ID</th>
                                <th>Settlement Amount</th>
                                <th>Status</th>
                                <th>Requested Date</th>
                                <th>Settled Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {orderData?.settlements?.map((settlement) => {
                                const statusClass =
                                  {
                                    SETTLED: "text-success",
                                    TO_BE_INITIATED: "text-secondary",
                                    INITIATED: "text-info",
                                    FAILED: "text-danger",
                                    PENDING: "text-warning",
                                  }[settlement.settlement_status] ||
                                  "text-light";

                                return (
                                  <tr key={settlement._id}>
                                    <td>{settlement.settlement_id}</td>
                                    <td>
                                      ₹{settlement.inter_participant_amount}
                                    </td>
                                    <td>
                                      <span
                                        className={`px-2 py-1 ${statusClass}`}
                                      >
                                        {settlement.settlement_status
                                          ?.toLowerCase()
                                          .replace(/_/g, " ")}
                                      </span>
                                    </td>
                                    <td>{formatDate(settlement.createdAt)}</td>
                                    <td>{formatDate(settlement.updatedAt)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>
      ) : (
        <UnAuthorized />
      )}
    </>
  );
}

export default OrderDetails;
